import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, XCircle, ExternalLink, Clock, Wallet, Paperclip, Image as ImageIcon, Languages, PartyPopper, BellPlus, BellRing } from "lucide-react";
import { T } from "@/lib/testIds";
import { LANGS, makeT, supportedLang, detectBrowserLang } from "@/lib/portalI18n";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const fmt = (amt, currency = "INR", locale = "en-IN") =>
  new Intl.NumberFormat(locale, { style: "currency", currency, maximumFractionDigits: 0 }).format(Number(amt || 0));

const localeMap = { en: "en-IN", hi: "hi-IN", ta: "ta-IN", mr: "mr-IN" };

function LangSwitcher({ lang, setLang, ariaLabel }) {
  return (
    <div
      className="flex items-center gap-1 border co-hairline rounded-sm p-0.5 bg-white"
      data-testid={T.portalLangSwitcher}
    >
      <Languages className="h-3.5 w-3.5 mx-1.5 text-[#52525B]" strokeWidth={1.75} aria-label={ariaLabel} />
      {LANGS.map((l) => (
        <button
          key={l.code}
          type="button"
          onClick={() => setLang(l.code)}
          data-testid={`${T.portalLangOption}-${l.code}`}
          className={`px-2 py-1 text-xs font-semibold rounded-sm transition-colors duration-200 ${
            lang === l.code ? "bg-[#4338CA] text-white" : "text-[#52525B] hover:bg-[#F4F4F5]"
          }`}
          aria-label={l.label}
          title={l.label}
        >
          {l.short}
        </button>
      ))}
    </div>
  );
}

const pill = (s) =>
  s === "paid" ? "co-pill co-pill-paid" :
  s === "overdue" ? "co-pill co-pill-overdue" :
  s === "pending_review" ? "co-pill co-pill-mock" : "co-pill co-pill-pending";

const statusKey = (s) =>
  s === "paid" ? "status_paid" :
  s === "overdue" ? "status_overdue" :
  s === "pending_review" ? "status_pending_review" : "status_pending";

function fireConfetti() {
  const colors = ["#4338CA", "#15803D", "#F59E0B", "#EC4899", "#38BDF8"];
  const defaults = { spread: 60, ticks: 90, gravity: 0.9, decay: 0.94, startVelocity: 32, colors };
  // Center burst
  confetti({ ...defaults, particleCount: 90, origin: { x: 0.5, y: 0.35 }, scalar: 1.1 });
  // Side bursts staggered
  setTimeout(() => {
    confetti({ ...defaults, particleCount: 40, angle: 60, origin: { x: 0.1, y: 0.6 } });
    confetti({ ...defaults, particleCount: 40, angle: 120, origin: { x: 0.9, y: 0.6 } });
  }, 180);
  setTimeout(() => {
    confetti({ ...defaults, particleCount: 30, spread: 100, origin: { x: 0.5, y: 0.5 }, scalar: 0.8 });
  }, 380);
}

export default function ClientPortal() {
  const { token } = useParams();
  const [lang, setLang] = useState(() => {
    const stored = localStorage.getItem("co_portal_lang");
    if (stored) return supportedLang(stored);
    return detectBrowserLang();
  });
  const [state, setState] = useState({ status: "loading", data: null, error: "" });
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [snoozing, setSnoozing] = useState(false);
  const [snoozeUntil, setSnoozeUntil] = useState(null);
  const fileRef = useRef(null);

  const t = makeT(lang, { business: state.data?.workspace?.name || "" });

  useEffect(() => {
    localStorage.setItem("co_portal_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/portal/${token}`);
      setState({ status: "ready", data: r.data, error: "" });
      setSnoozeUntil(r.data?.client?.snooze_reminder_until || null);
    } catch (e) {
      setState({
        status: "error",
        data: null,
        error: e?.response?.data?.detail || "Portal link is invalid.",
      });
    }
  };

  useEffect(() => { load(); }, [token]);

  const snoozeTomorrow = async () => {
    setSnoozing(true);
    try {
      const r = await axios.post(`${API}/portal/${token}/snooze`, { days: 1 });
      setSnoozeUntil(r.data?.snooze_until || null);
      toast.success(t("remind_scheduled").replace("{date}", r.data?.snooze_until || ""));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("remind_err"));
    } finally {
      setSnoozing(false);
    }
  };

  const onPickReceipt = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error(t("err_only_image"));
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error(t("err_size"));
      return;
    }
    setReceipt(f);
    setReceiptPreview(URL.createObjectURL(f));
  };

  const clearReceipt = () => {
    setReceipt(null);
    if (receiptPreview) URL.revokeObjectURL(receiptPreview);
    setReceiptPreview(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submitConfirm = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("reference", reference);
      if (note) fd.append("note", note);
      if (receipt) fd.append("receipt", receipt);
      await axios.post(`${API}/portal/${token}/self-confirm`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setConfirmed(true);
      clearReceipt();
      fireConfetti();
      toast.success(receipt ? t("thank_you_with_receipt") : t("thank_you_no_receipt"));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("err_submit"));
    } finally {
      setBusy(false);
    }
  };

  if (state.status === "loading") {
    return <div className="min-h-screen grid place-items-center text-sm text-[#52525B]">{t("loading")}</div>;
  }
  if (state.status === "error") {
    return (
      <div className="min-h-screen grid place-items-center px-6">
        <div className="max-w-md w-full border co-hairline bg-white p-8 text-center">
          <XCircle className="h-10 w-10 mx-auto text-[#B91C1C]" strokeWidth={1.5} />
          <h1 className="font-heading text-2xl font-bold tracking-tight mt-4">{t("link_unavailable")}</h1>
          <p className="text-sm text-[#52525B] mt-2">{state.error}</p>
        </div>
      </div>
    );
  }

  const { client, workspace, payment_options, recent_payments } = state.data;
  const locale = localeMap[lang] || "en-IN";
  const cycleLabel = t(client.billing_cycle) || client.billing_cycle;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="co-glass-header sticky top-0 z-20 px-6 lg:px-10 py-4 flex items-center gap-3 justify-between flex-wrap">
        <div className="flex items-center gap-3">
          <img src="/collectone-logo.webp" alt="CollectOne" className="w-8 h-8 object-contain" />
          <div>
            <div className="co-label">{t("payment_portal")}</div>
            <div className="font-heading font-bold tracking-tight">{workspace.name}</div>
          </div>
        </div>
        <LangSwitcher lang={lang} setLang={setLang} ariaLabel={t("language_label")} />
      </header>

      <main className="max-w-3xl mx-auto p-6 lg:p-10 space-y-6">
        <section className="border co-hairline bg-white p-6 lg:p-8">
          <div className="co-label">{t("hello")}, {client.name}</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">
            {client.product_name}
          </h1>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-6 co-grid-borders">
            <div>
              <div className="co-label">{t("amount_due")}</div>
              <div className="font-heading text-2xl font-bold tracking-tighter mt-1">
                {fmt(client.amount, workspace.currency, locale)}
              </div>
            </div>
            <div>
              <div className="co-label">{t("cycle")}</div>
              <div className="font-heading text-lg font-bold tracking-tight mt-1">
                {cycleLabel}
              </div>
            </div>
            <div>
              <div className="co-label">{t("due_date")}</div>
              <div className="font-heading text-lg font-bold tracking-tight mt-1 font-mono">
                {client.next_due_date}
              </div>
            </div>
            <div>
              <div className="co-label">{t("status")}</div>
              <div className="mt-2">
                <span className={pill(client.payment_status)}>
                  {t(statusKey(client.payment_status))}
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="border co-hairline bg-white">
          <div className="p-6 lg:p-8 border-b co-hairline flex items-center gap-2">
            <Wallet className="h-4 w-4 text-[#4338CA]" strokeWidth={1.75} />
            <div className="font-heading text-xl font-bold tracking-tight">{t("ways_to_pay")}</div>
          </div>
          {payment_options.length === 0 ? (
            <div className="p-8 text-sm text-[#52525B]">
              {t("no_options")}
            </div>
          ) : (
            <ul>
              {payment_options.map((p, i) => (
                <li
                  key={i}
                  className={`flex items-center justify-between p-5 gap-4 ${
                    i < payment_options.length - 1 ? "border-b co-hairline" : ""
                  }`}
                >
                  <div className="min-w-0">
                    <div className="font-semibold">{p.label}</div>
                    <div className="text-xs text-[#52525B] truncate max-w-[420px]">{p.url}</div>
                  </div>
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={T.portalPayLink}
                    className="inline-flex items-center gap-1.5 bg-[#4338CA] hover:bg-[#3730A3] text-white px-4 py-2 text-sm font-semibold rounded-sm co-btn-press transition-colors duration-200 whitespace-nowrap"
                  >
                    {t("pay")} <ExternalLink className="h-3.5 w-3.5" strokeWidth={2} />
                  </a>
                </li>
              ))}
            </ul>
          )}
        </section>

        {!confirmed && client.payment_status !== "paid" && (
          <section className="border co-hairline bg-white p-6 lg:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="min-w-0">
              <div className="co-label flex items-center gap-2">
                <BellPlus className="h-3.5 w-3.5" strokeWidth={2} />
                {t("remind_title")}
              </div>
              <p className="text-sm text-[#52525B] mt-2 max-w-xl">{t("remind_desc")}</p>
              {snoozeUntil && (
                <p
                  data-testid={T.portalSnoozeStatus}
                  className="text-xs font-semibold text-[#15803D] mt-2 inline-flex items-center gap-1"
                >
                  <BellRing className="h-3.5 w-3.5" strokeWidth={2} />
                  {t("remind_scheduled").replace("{date}", snoozeUntil)}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={snoozeTomorrow}
              disabled={snoozing || !!snoozeUntil}
              data-testid={T.portalSnoozeBtn}
              className="inline-flex items-center gap-2 border co-hairline bg-white hover:bg-[#FAFAFA] text-[#0A0A0B] px-4 py-2.5 text-sm font-semibold rounded-sm co-btn-press disabled:opacity-50 whitespace-nowrap transition-colors duration-200"
            >
              <BellPlus className="h-4 w-4 text-[#4338CA]" strokeWidth={2} />
              {snoozing ? t("remind_scheduling") : t("remind_me_tomorrow")}
            </button>
          </section>
        )}

        {confirmed && (
          <section
            data-testid={T.portalThankYou}
            className="border co-hairline bg-white p-6 lg:p-10 text-center co-fade-in relative overflow-hidden"
          >
            <div className="absolute inset-0 pointer-events-none opacity-70 bg-[radial-gradient(circle_at_top,rgba(67,56,202,0.10),transparent_60%)]" />
            <div className="relative">
              <div className="w-14 h-14 mx-auto bg-[#15803D] text-white rounded-full grid place-items-center">
                <PartyPopper className="h-7 w-7" strokeWidth={1.5} />
              </div>
              <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-bold tracking-tighter">
                {t("ty_headline")}
              </h2>
              <p className="mt-3 text-sm sm:text-base text-[#52525B] max-w-md mx-auto">
                {t("ty_sub")}
              </p>
              <p className="mt-2 text-xs text-[#52525B] max-w-md mx-auto">
                {t("ty_next_steps")}
              </p>
              <button
                type="button"
                onClick={() => {
                  setConfirmed(false);
                  setReference("");
                  setNote("");
                }}
                data-testid={T.portalReportAnother}
                className="mt-6 inline-flex items-center gap-2 text-xs font-semibold text-[#4338CA] hover:text-[#3730A3]"
              >
                {t("report_another")}
              </button>
            </div>
          </section>
        )}

        {!confirmed && (
          <section className="border co-hairline bg-white p-6 lg:p-8">
            <div className="co-label">{t("already_paid_title")}</div>
            <div className="font-heading text-xl font-bold tracking-tight mt-1">{t("already_paid_sub")}</div>
            <p className="text-sm text-[#52525B] mt-2">{t("already_paid_desc")}</p>
            <form onSubmit={submitConfirm} className="mt-5 space-y-4">
              <div>
                <Label className="co-label">{t("reference_label")}</Label>
                <Input
                  data-testid={T.portalReference}
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  required
                  className="mt-2 h-11"
                  placeholder={t("reference_ph")}
                />
              </div>
              <div>
                <Label className="co-label">{t("note_label")}</Label>
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="mt-2"
                  rows={2}
                  placeholder={t("note_ph")}
                />
              </div>
              <div>
                <Label className="co-label">{t("receipt_label")}</Label>
                <input
                  type="file"
                  ref={fileRef}
                  accept="image/*"
                  onChange={onPickReceipt}
                  data-testid={T.portalReceiptInput}
                  className="hidden"
                />
                {!receipt ? (
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="mt-2 w-full border border-dashed co-hairline rounded-sm p-6 flex flex-col items-center gap-2 text-sm text-[#52525B] hover:bg-[#FAFAFA] transition-colors duration-200"
                  >
                    <Paperclip className="h-5 w-5 text-[#4338CA]" strokeWidth={1.75} />
                    <span>{t("receipt_hint")}</span>
                    <span className="text-xs">{t("receipt_size")}</span>
                  </button>
                ) : (
                  <div className="mt-2 border co-hairline rounded-sm p-3 flex items-center gap-3">
                    {receiptPreview ? (
                      <img
                        src={receiptPreview}
                        alt="Receipt preview"
                        className="w-16 h-16 object-cover rounded-sm border co-hairline"
                      />
                    ) : (
                      <ImageIcon className="h-10 w-10 text-[#52525B]" strokeWidth={1.25} />
                    )}
                    <div className="flex-1 min-w-0 text-sm">
                      <div className="font-semibold truncate">{receipt.name}</div>
                      <div className="text-xs text-[#52525B]">
                        {(receipt.size / 1024).toFixed(0)} KB
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={clearReceipt}
                      className="text-xs font-semibold text-[#B91C1C] hover:text-[#991B1B]"
                    >
                      {t("remove")}
                    </button>
                  </div>
                )}
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid={T.portalConfirmSubmit}
                className="inline-flex items-center gap-2 bg-[#15803D] hover:bg-[#166534] text-white px-4 py-2.5 text-sm font-semibold rounded-sm co-btn-press disabled:opacity-60 transition-colors duration-200"
              >
                <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
                {busy ? t("reporting") : t("i_have_paid")}
              </button>
            </form>
          </section>
        )}

        {recent_payments.length > 0 && (
          <section className="border co-hairline bg-white p-6 lg:p-8">
            <div className="co-label flex items-center gap-2">
              <Clock className="h-3.5 w-3.5" strokeWidth={2} /> {t("recent_payments")}
            </div>
            <ul className="mt-4 space-y-2 text-sm">
              {recent_payments.map((p) => (
                <li key={p.id} className="flex items-center justify-between border-b co-hairline pb-2">
                  <div>
                    <div className="font-semibold">{fmt(p.amount, workspace.currency, locale)}</div>
                    <div className="text-xs text-[#52525B]">
                      {(t(p.method === "upi" ? "upi_method" : p.method) || p.method).toString().replace("_", " ")} · {new Date(p.created_at).toLocaleDateString(locale)}
                    </div>
                  </div>
                  <span className={p.status === "success" ? "co-pill co-pill-paid" : "co-pill co-pill-mock"}>
                    {t(p.status === "success" ? "status_paid" : "status_pending_review")}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <footer className="text-center text-xs text-[#52525B] py-6">
          {t("powered_by")} <span className="font-semibold text-[#0A0A0B]">CollectOne</span>
        </footer>
      </main>
    </div>
  );
}
